import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Search, MessageCircle, FolderUp, CreditCard, PawPrint } from "lucide-react";
import { api, errMsg, eur, fmtDate, todayRome } from "@/lib/api";
import { BRAND, PAY_STATUS, SERVICES, waLink } from "@/lib/brand";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, Empty, PageHead, Pill } from "@/components/common";
import { EMPTY_PRATICA, PraticaDialog } from "@/components/admin/PraticaDialog";
import { VetDialog } from "@/components/admin/VetDialog";
import { VetFilesDialog } from "@/components/admin/VetFilesDialog";
import { PromemoriaDialog } from "@/components/admin/PromemoriaDialog";

const match = (obj, q, keys) => !q || keys.some((k) => String(obj[k] || "").toLowerCase().includes(q.toLowerCase()));

const ClientiTable = ({ rows, onEdit, onDelete, onDdt, onWa, onPayLink, onNewPet }) => (
  rows.length === 0 ? <Empty>Nessuna anagrafica cliente.</Empty> : (
    <Card className="overflow-x-auto">
      <Table data-testid="clienti-table">
        <TableHeader><TableRow>
          <TableHead>Cliente</TableHead><TableHead>Animale</TableHead><TableHead>Servizio</TableHead><TableHead>Ritiro</TableHead>
          <TableHead className="text-right">Totale</TableHead><TableHead className="text-right">Acconto</TableHead><TableHead className="text-right">Saldo</TableHead>
          <TableHead className="text-right">Residuo</TableHead><TableHead>Stato</TableHead><TableHead />
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id} data-testid={`cliente-row-${p.id}`}>
              <TableCell><p className="font-semibold text-[#0D3B52]">{p.nome}</p><p className="text-xs text-[#5C6B73]">{p.telefono}{p.vet_name ? ` · ${p.vet_name}` : ""}</p></TableCell>
              <TableCell>{p.animale_nome || "—"} <span className="text-xs text-[#5C6B73]">{p.specie}</span></TableCell>
              <TableCell className="whitespace-nowrap">{SERVICES[p.servizio]}</TableCell>
              <TableCell className="whitespace-nowrap">{fmtDate(p.data_ritiro)}</TableCell>
              <TableCell className="text-right whitespace-nowrap">{eur(p.totale)}</TableCell>
              <TableCell className="text-right whitespace-nowrap">{eur(p.acconto)}</TableCell>
              <TableCell className="text-right whitespace-nowrap">{eur(p.saldo)}</TableCell>
              <TableCell className={`text-right whitespace-nowrap font-semibold ${p.residuo > 0 ? "text-rose-700" : "text-emerald-700"}`}>{eur(p.residuo)}</TableCell>
              <TableCell><Pill cls={PAY_STATUS[p.stato_pagamento].cls}>{PAY_STATUS[p.stato_pagamento].label}</Pill></TableCell>
              <TableCell className="whitespace-nowrap text-right">
                <Button size="sm" variant="outline" onClick={() => onNewPet(p)} data-testid={`cliente-newpet-${p.id}`} className="mr-1 rounded-full"><PawPrint className="h-4 w-4 mr-1" />Nuovo animale</Button>
                {p.residuo > 0 && <Button size="sm" variant="outline" onClick={() => onPayLink(p)} data-testid={`cliente-paylink-${p.id}`} className="mr-1 rounded-full border-[#0D3B52] text-[#0D3B52]"><CreditCard className="h-4 w-4 mr-1" />Link pagamento</Button>}
                <Button size="sm" onClick={() => onWa(p)} data-testid={`cliente-wa-${p.id}`} className="mr-1 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white"><MessageCircle className="h-4 w-4 mr-1" />Promemoria</Button>
                <Button size="sm" variant="outline" onClick={() => onDdt(p)} data-testid={`cliente-ddt-${p.id}`} className="mr-1"><FileText className="h-4 w-4 mr-1" />{p.ddt_id ? "Apri DDT" : "Crea DDT"}</Button>
                <Button size="icon" variant="ghost" onClick={() => onEdit(p)} data-testid={`cliente-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => onDelete(p)} data-testid={`cliente-delete-${p.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )
);

const VetTable = ({ rows, onEdit, onDelete, onFiles, onCred, onNewPet }) => (
  rows.length === 0 ? <Empty>Nessun veterinario registrato.</Empty> : (
    <Card className="overflow-x-auto">
      <Table data-testid="vets-table">
        <TableHeader><TableRow>
          <TableHead>Clinica</TableHead><TableHead>Contatti</TableHead><TableHead>Indirizzo</TableHead><TableHead className="text-right">Pratiche</TableHead>
          <TableHead className="text-right">Totale</TableHead><TableHead className="text-right">Incassato</TableHead><TableHead className="text-right">Da incassare</TableHead><TableHead />
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((v) => (
            <TableRow key={v.id} data-testid={`vet-row-${v.id}`}>
              <TableCell><p className="font-semibold text-[#0D3B52]">{v.clinic_name}</p><p className="text-xs text-[#5C6B73]">{v.name}{v.active === false ? " · disattivato" : ""}</p></TableCell>
              <TableCell><p className="text-sm">{v.email}</p><p className="text-xs text-[#5C6B73]">{v.phone}</p></TableCell>
              <TableCell className="text-sm">{v.address} {v.city}</TableCell>
              <TableCell className="text-right">{v.pratiche_count}</TableCell>
              <TableCell className="text-right whitespace-nowrap">{eur(v.totale)}</TableCell>
              <TableCell className="text-right whitespace-nowrap">{eur(v.incassato)}</TableCell>
              <TableCell className={`text-right whitespace-nowrap font-semibold ${v.residuo > 0 ? "text-rose-700" : ""}`}>{eur(v.residuo)}</TableCell>
              <TableCell className="whitespace-nowrap text-right">
                <Button size="sm" variant="outline" onClick={() => onNewPet(v)} data-testid={`vet-newpet-${v.id}`} className="rounded-full mr-1"><PawPrint className="h-4 w-4 mr-1" />Nuovo animale</Button>
                <Button size="sm" onClick={() => onCred(v)} data-testid={`vet-cred-${v.id}`} className="rounded-full mr-1 bg-[#25D366] hover:bg-[#1DA851] text-white"><MessageCircle className="h-4 w-4 mr-1" />Credenziali</Button>
                <Button size="sm" variant="outline" onClick={() => onFiles(v)} data-testid={`vet-files-${v.id}`} className="rounded-full mr-1"><FolderUp className="h-4 w-4 mr-1" />File PDF</Button>
                <Button size="icon" variant="ghost" onClick={() => onEdit(v)} data-testid={`vet-edit-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => onDelete(v)} data-testid={`vet-delete-${v.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )
);

export default function Anagrafica() {
  const nav = useNavigate();
  const [pratiche, setPratiche] = useState([]);
  const [vets, setVets] = useState([]);
  const [q, setQ] = useState("");
  const [pEdit, setPEdit] = useState(null);
  const [vEdit, setVEdit] = useState(null);
  const [vFiles, setVFiles] = useState(null);
  const [created, setCreated] = useState(null);
  const [wa, setWa] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/pratiche").then((r) => setPratiche(r.data));
    api.get("/admin/vets").then((r) => setVets(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const fp = useMemo(() => pratiche.filter((p) => match(p, q, ["nome", "telefono", "animale_nome", "microchip", "indirizzo", "vet_name"])), [pratiche, q]);
  const fv = useMemo(() => vets.filter((v) => match(v, q, ["clinic_name", "name", "email", "city"])), [vets, q]);

  const delP = async (p) => { if (window.confirm(`Eliminare l'anagrafica di ${p.nome}?`)) { await api.delete(`/admin/pratiche/${p.id}`); load(); } };
  const delV = async (v) => {
    if (!window.confirm(`Eliminare ${v.clinic_name}? Il veterinario non potrà più accedere.`)) return;
    try { await api.delete(`/admin/vets/${v.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onDdt = (p) => nav(p.ddt_id ? `/admin/ddt/${p.ddt_id}` : `/admin/ddt/nuovo?pratica=${p.id}`);
  const newPetFromClient = (p) => setPEdit({ ...EMPTY_PRATICA, tipo_cliente: p.tipo_cliente || "privato", vet_id: p.vet_id || "", nome: p.nome, telefono: p.telefono || "", email: p.email || "",
    indirizzo: p.indirizzo || "", citta: p.citta || "", codice_fiscale: p.codice_fiscale || "", data_ritiro: todayRome() });
  const newPetFromVet = (v) => setPEdit({ ...EMPTY_PRATICA, tipo_cliente: "veterinario", vet_id: v.id, nome: v.clinic_name || v.name, telefono: v.phone || "", email: v.email || "",
    indirizzo: v.address || "", citta: v.city || "", data_ritiro: todayRome() });
  const payLink = async (p) => {
    const klarna = window.confirm("Il cliente pagherà con Klarna in 3 rate? (OK = Klarna con +6% commissioni, Annulla = carta)");
    try {
      const { data } = await api.post("/payments/checkout", { pratica_id: p.id, klarna, origin_url: window.location.origin });
      const msg = `Gentile ${p.nome}, può saldare il servizio per ${p.animale_nome || "il suo piccolo"} (${eur(data.amount)}) in modo sicuro da questo link:\n${data.checkout_url}\n\nAgenzia Funebre per Animali – 389 244 6910`;
      window.open(waLink(p.telefono, msg), "_blank");
      toast.success(`Link di pagamento creato (${eur(data.amount)})`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const sendCred = async (v) => {
    if (!window.confirm(`Generare una nuova password per ${v.clinic_name} e inviarla su WhatsApp? La vecchia password non funzionerà più.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${v.id}/reset-password`);
      setCreated({ ...v, password: data.password });
      if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank");
      else toast.warning("Nessun telefono: copia le credenziali dal riquadro");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const vetSaved = (v, pwd) => { load(); if (pwd) setCreated({ ...v, password: pwd }); };

  return (
    <div data-testid="admin-anagrafica">
      <PageHead eyebrow="Anagrafica" title="Clienti e veterinari" subtitle="Tutti i dati, gli acconti e i saldi. Da ogni cliente puoi generare il documento di trasporto SOA.">
        <Button onClick={() => setPEdit({})} data-testid="anagrafica-new-cliente" className="rounded-full bg-[#0D3B52] hover:bg-[#082535]"><Plus className="h-4 w-4 mr-1" />Nuovo cliente</Button>
        <Button onClick={() => setVEdit({})} variant="outline" data-testid="anagrafica-new-vet" className="rounded-full"><Plus className="h-4 w-4 mr-1" />Nuovo veterinario</Button>
        <Button onClick={() => setWa({})} data-testid="anagrafica-wa-libero" className="rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white"><MessageCircle className="h-4 w-4 mr-1" />Promemoria a nuovo numero</Button>
      </PageHead>
      <div className="relative max-w-sm mb-6">
        <Search className="h-4 w-4 absolute left-3 top-3 text-[#5C6B73]" />
        <Input placeholder="Cerca per nome, telefono, microchip…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="anagrafica-search" />
      </div>
      <Tabs defaultValue="clienti">
        <TabsList className="bg-white border border-[#E2DDD5]">
          <TabsTrigger value="clienti" data-testid="anagrafica-tab-clienti" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white">Clienti ({fp.length})</TabsTrigger>
          <TabsTrigger value="vet" data-testid="anagrafica-tab-vet" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white">Veterinari ({fv.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="clienti" className="mt-6"><ClientiTable rows={fp} onEdit={setPEdit} onDelete={delP} onDdt={onDdt} onWa={setWa} onPayLink={payLink} onNewPet={newPetFromClient} /></TabsContent>
        <TabsContent value="vet" className="mt-6"><VetTable rows={fv} onEdit={setVEdit} onDelete={delV} onFiles={setVFiles} onCred={sendCred} onNewPet={newPetFromVet} /></TabsContent>
      </Tabs>
      <PromemoriaDialog pratica={wa} open={!!wa} onOpenChange={(o) => !o && setWa(null)} />
      <VetFilesDialog vet={vFiles} onOpenChange={(o) => !o && setVFiles(null)} />
      <PraticaDialog open={!!pEdit} onOpenChange={(o) => !o && setPEdit(null)} initial={pEdit} vets={vets} onSaved={(d) => { load(); if (d && !d.ddt_id && window.confirm("Animale salvato. Vuoi creare subito il DDT?")) onDdt(d); }} />
      <VetDialog open={!!vEdit} onOpenChange={(o) => !o && setVEdit(null)} initial={vEdit} onSaved={vetSaved} />
      {created && (
        <Card className="fixed bottom-6 right-6 z-50 p-6 max-w-sm shadow-2xl" data-testid="vet-credentials-card">
          <p className="font-display text-xl font-semibold text-[#0D3B52]">Credenziali per {created.clinic_name}</p>
          <p className="text-sm mt-2">Email: <strong>{created.email}</strong><br />Password: <strong>{created.password}</strong></p>
          <div className="flex gap-2 mt-4">
            <a href={waLink(created.phone, `Buongiorno, ecco le credenziali per l'area riservata di ${BRAND.name}:\n${window.location.origin}/login\nEmail: ${created.email}\nPassword: ${created.password}`)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-[#25D366] px-4 py-2 text-sm text-white" data-testid="vet-credentials-whatsapp"><MessageCircle className="h-4 w-4" />Invia su WhatsApp</a>
            <Button variant="ghost" size="sm" onClick={() => setCreated(null)}>Chiudi</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
